import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useCreateWikiPage, useEmployees, useReorderWiki, useUpdateWikiPage, useWiki } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Roles } from '@touristation/shared';
import type { WikiPage } from '../api/types';
import { useLang, useT } from '../i18n/LangProvider';
import { Icon } from '../ui/Icon';
import { Query } from '../ui/atoms';
import { RichTextEditor } from '../ui/RichTextEditor';
import { addHeadingIds, htmlToText, mdToHtml, tocFromHtml } from '../ui/markdown';
import { useDialogFocus } from '../ui/useDialogFocus';

/**
 * Il body è Markdown. Le pagine salvate prima della conversione contengono HTML:
 * markdown-it lo lascia passare come blocco HTML, quindi continuano a
 * visualizzarsi e diventano Markdown alla prima modifica.
 */
const renderBody = (body: string, pages: WikiPage[]) => addHeadingIds(mdToHtml(body, pages));

const initials = (s: string) => s.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `pagina-${Date.now().toString().slice(-5)}`;

type DropPos = 'before' | 'inside' | 'after';
type MoveItem = { id: string; parentId: string | null; order: number };

/**
 * Re-number the destination parent's children with the dragged page inserted at
 * the drop point. Sending the whole sibling list keeps `order` dense and stable.
 */
function planMove(pages: WikiPage[], dragId: string, targetId: string, pos: DropPos): MoveItem[] {
  const dragged = pages.find((p) => p.id === dragId);
  const target = pages.find((p) => p.id === targetId);
  if (!dragged || !target) return [];

  const parentId = pos === 'inside' ? target.id : target.parentId;
  const sibs = pages
    .filter((p) => p.parentId === parentId && p.id !== dragId)
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));

  let index = sibs.length;
  if (pos !== 'inside') {
    const at = sibs.findIndex((p) => p.id === targetId);
    index = pos === 'before' ? at : at + 1;
  }
  const next = [...sibs];
  next.splice(index, 0, dragged);
  return next.map((p, i) => ({ id: p.id, parentId: parentId ?? null, order: i }));
}

/** True when `maybeChild` sits somewhere under `ancestorId` — an illegal drop target. */
function isDescendant(pages: WikiPage[], ancestorId: string, maybeChild: string | null): boolean {
  let cursor = maybeChild;
  let hops = 0;
  while (cursor) {
    if (cursor === ancestorId) return true;
    if (++hops > pages.length) return false;
    cursor = pages.find((p) => p.id === cursor)?.parentId ?? null;
  }
  return false;
}

function Tree({ pages, currentId, query, expanded, onToggle, onSelect, onAddSub, canReorder, onMove }: {
  pages: WikiPage[]; currentId: string | null; query: string;
  expanded: Record<string, boolean>; onToggle: (id: string) => void; onSelect: (id: string) => void; onAddSub: (parentId: string) => void;
  canReorder: boolean; onMove: (items: MoveItem[]) => void;
}) {
  const { lang, t: tr } = useLang();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; pos: DropPos } | null>(null);
  const [mobileFolderId, setMobileFolderId] = useState<string | null>(null);
  const [mobileSection, setMobileSection] = useState<'pages' | 'starred'>('pages');

  const children = (id: string | null) => pages
    .filter((p) => p.parentId === id)
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  const starred = pages.filter((p) => p.starred);
  const matches = (p: WikiPage) => !query || p.title.toLowerCase().includes(query.toLowerCase()) || p.body.toLowerCase().includes(query.toLowerCase()) || p.tags.some((t) => t.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const selected = pages.find((p) => p.id === currentId);
    setMobileFolderId(selected?.isFolder ? selected.id : (selected?.parentId ?? null));
  }, [currentId, pages]);

  const mobileFolder = pages.find((p) => p.id === mobileFolderId) ?? null;
  const mobileItems = query
    ? pages.filter(matches).sort((a, b) => a.title.localeCompare(b.title))
    : mobileSection === 'starred'
      ? starred.sort((a, b) => a.title.localeCompare(b.title))
      : children(mobileFolderId);
  const pagePath = (page: WikiPage) => {
    const names: string[] = [];
    let parentId = page.parentId;
    let hops = 0;
    while (parentId && hops++ < pages.length) {
      const parent = pages.find((item) => item.id === parentId);
      if (!parent) break;
      names.unshift(parent.title);
      parentId = parent.parentId;
    }
    return names.join(' / ') || tr('Primo livello');
  };

  // Reordering a filtered tree would be meaningless, so it is disabled while searching.
  const dnd = canReorder && !query;

  const canDropOn = (targetId: string, pos: DropPos) => {
    if (!dragId || dragId === targetId) return false;
    const target = pages.find((p) => p.id === targetId);
    if (!target) return false;
    const destParent = pos === 'inside' ? target.id : target.parentId;
    return !isDescendant(pages, dragId, destParent);
  };

  /** Top/bottom quarter of the row reorders; the middle nests inside. */
  const posFromEvent = (e: React.DragEvent<HTMLDivElement>): DropPos => {
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    return y < 0.28 ? 'before' : y > 0.72 ? 'after' : 'inside';
  };

  const node = (p: WikiPage, depth: number) => {
    const subs = children(p.id);
    if (query && !matches(p) && !subs.some(matches)) return null;
    const isOpen = expanded[p.id] !== false;
    const drop = dropAt?.id === p.id ? dropAt.pos : null;
    return (
      <div key={p.id}>
        <div
          className={[
            'wiki-tree__row',
            currentId === p.id ? 'is-active' : '',
            dragId === p.id ? 'is-dragging' : '',
            drop ? `is-drop-${drop}` : '',
          ].filter(Boolean).join(' ')}
          style={{ paddingLeft: 8 + depth * 14 }}
          draggable={dnd}
          onDragStart={(e) => { setDragId(p.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', p.id); }}
          onDragEnd={() => { setDragId(null); setDropAt(null); }}
          onDragOver={(e) => {
            if (!dnd || !dragId) return;
            const pos = posFromEvent(e);
            if (!canDropOn(p.id, pos)) { setDropAt(null); return; }
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropAt({ id: p.id, pos });
          }}
          onDragLeave={() => setDropAt((d) => (d?.id === p.id ? null : d))}
          onDrop={(e) => {
            if (!dnd || !dragId) return;
            e.preventDefault();
            const pos = posFromEvent(e);
            if (canDropOn(p.id, pos)) {
              const items = planMove(pages, dragId, p.id, pos);
              if (items.length) onMove(items);
              if (pos === 'inside' && expanded[p.id] === false) onToggle(p.id);
            }
            setDragId(null); setDropAt(null);
          }}
        >
          {subs.length > 0
            ? (
              <button
                className="wiki-tree__caret"
                onClick={() => onToggle(p.id)}
                aria-label={`${isOpen ? tr('Comprimi') : tr('Espandi')} ${p.title}`}
                aria-expanded={isOpen}
              >
                <Icon name={isOpen ? 'chevD' : 'chevR'} size={11} />
              </button>
            )
            : <span className="wiki-tree__caret" />}
          <button className="wiki-tree__label" onClick={() => onSelect(p.id)}>
            <span className="wiki-tree__icon">{p.isFolder ? '📁' : (p.icon || '📄')}</span>
            <span className="wiki-tree__title">{p.title}</span>
            {p.starred && <span className="wiki-tree__star">★</span>}
          </button>
          <button className="wiki-tree__plus" title={tr('Nuova sotto-pagina')} onClick={() => onAddSub(p.id)}><Icon name="plus" size={11} /></button>
        </div>
        {isOpen && subs.map((s) => node(s, depth + 1))}
      </div>
    );
  };
  return (
    <>
      <div className="wiki-tree wiki-tree--desktop">
        {starred.length > 0 && !query && (
          <>
            <div className="wiki-tree__section">{tr('Preferiti')}</div>
            {starred.map((p) => (
              <div key={`f-${p.id}`} className={`wiki-tree__row ${currentId === p.id ? 'is-active' : ''}`} style={{ paddingLeft: 8 }}>
                <span className="wiki-tree__caret" />
                <button className="wiki-tree__label" onClick={() => onSelect(p.id)}>
                  <span className="wiki-tree__icon">{p.icon || '📄'}</span><span className="wiki-tree__title">{p.title}</span><span className="wiki-tree__star">★</span>
                </button>
              </div>
            ))}
          </>
        )}
        <div className="wiki-tree__section">
          {tr('Tutte le pagine')}
          {dnd && <span className="wiki-tree__hint" title={tr('Trascina per riordinare o annidare')}>⇅</span>}
        </div>
        {children(null).map((r) => node(r, 0))}

        {/* Landing strip: drop here to pull a page back out to the top level. */}
        {dnd && dragId && (
          <div
            className={`wiki-tree__root-drop ${dropAt?.id === '__root__' ? 'is-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDropAt({ id: '__root__', pos: 'after' }); }}
            onDragLeave={() => setDropAt((d) => (d?.id === '__root__' ? null : d))}
            onDrop={(e) => {
              e.preventDefault();
              const roots = children(null).filter((p) => p.id !== dragId);
              const dragged = pages.find((p) => p.id === dragId);
              if (dragged) {
                onMove([...roots, dragged].map((p, i) => ({ id: p.id, parentId: null, order: i })));
              }
              setDragId(null); setDropAt(null);
            }}
          >
            {tr('Sposta al primo livello')}
          </div>
        )}
      </div>

      <div className="wiki-mobile-browser">
        <div className="wiki-mobile-browser__tabs" role="tablist" aria-label={tr('Sezioni Wiki')}>
          <button
            role="tab"
            aria-selected={mobileSection === 'pages'}
            className={mobileSection === 'pages' ? 'is-active' : ''}
            onClick={() => setMobileSection('pages')}
          >
            <Icon name="folder" size={14} /> {tr('Pagine')}
          </button>
          <button
            role="tab"
            aria-selected={mobileSection === 'starred'}
            className={mobileSection === 'starred' ? 'is-active' : ''}
            onClick={() => setMobileSection('starred')}
          >
            <Icon name="star" size={14} /> {tr('Preferiti')}
            {starred.length > 0 && <span>{starred.length}</span>}
          </button>
        </div>

        {!query && mobileSection === 'pages' && (
          <div className="wiki-mobile-browser__path">
            {mobileFolder && (
              <button
                onClick={() => setMobileFolderId(mobileFolder.parentId)}
                aria-label={tr('Torna alla cartella precedente')}
              >
                <Icon name="chevL" size={16} />
              </button>
            )}
            <div>
              <strong>{mobileFolder?.title ?? tr('Tutte le pagine')}</strong>
              <span>{tr('{n} elementi', { n: mobileItems.length })}</span>
            </div>
          </div>
        )}

        <div className="sr-only" aria-live="polite">{tr('{n} risultati', { n: mobileItems.length })}</div>
        <div className="wiki-mobile-browser__list">
          {mobileItems.length === 0 && (
            <div className="wiki-mobile-browser__empty">
              <Icon name={mobileSection === 'starred' ? 'star' : 'folder'} size={22} />
              <span>{query ? tr('Nessun risultato.') : mobileSection === 'starred' ? tr('Nessuna pagina preferita.') : tr('Questa cartella è vuota.')}</span>
            </div>
          )}
          {mobileItems.map((page) => {
            const childCount = pages.filter((item) => item.parentId === page.id).length;
            const opensFolder = page.isFolder && mobileSection === 'pages' && !query;
            return (
              <div key={`mobile-${page.id}`} className={`wiki-mobile-browser__item ${currentId === page.id ? 'is-active' : ''}`}>
                <button
                  className="wiki-mobile-browser__main"
                  onClick={() => opensFolder ? setMobileFolderId(page.id) : onSelect(page.id)}
                  aria-current={currentId === page.id ? 'page' : undefined}
                >
                  <span className={`wiki-mobile-browser__icon ${page.isFolder ? 'is-folder' : ''}`}>
                    {page.isFolder ? <Icon name="folder" size={18} /> : <span>{page.icon || '📄'}</span>}
                  </span>
                  <span className="wiki-mobile-browser__copy">
                    <strong>{page.title}</strong>
                    <small>{query ? pagePath(page) : page.isFolder ? tr('{n} elementi', { n: childCount }) : tr('Agg. {date}', { date: new Date(page.updatedAt).toLocaleDateString(loc) })}</small>
                  </span>
                  {opensFolder ? <Icon name="chevR" size={16} /> : page.starred ? <Icon name="star" size={13} /> : null}
                </button>
                {opensFolder && (
                  <button
                    className="wiki-mobile-browser__open"
                    onClick={() => onSelect(page.id)}
                    aria-label={tr('Apri la pagina {title}', { title: page.title })}
                  >
                    <Icon name="eye" size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function Switcher({ pages, title, onPick, onClose }: { pages: WikiPage[]; title: string; onPick: (id: string) => void; onClose: () => void }) {
  const tr = useT();
  const [query, setQuery] = useState('');
  const filtered = query ? pages.filter((p) => p.title.toLowerCase().includes(query.toLowerCase()) || p.slug.includes(query.toLowerCase())).slice(0, 12) : pages.slice(0, 8);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'grid', placeItems: 'start center', paddingTop: '12vh', zIndex: 120 }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(520px, 92%)', padding: 0, overflow: 'hidden' }}>
        <div className="row" style={{ gap: 8, padding: 12, borderBottom: '1px solid var(--line,#eee)', alignItems: 'center' }}>
          <Icon name="search" size={15} />
          <input autoFocus className="input" style={{ border: 'none', flex: 1 }} placeholder={title} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter' && filtered[0]) onPick(filtered[0].id); }} />
          <span className="kbd">Esc</span>
        </div>
        <div style={{ maxHeight: 320, overflow: 'auto', padding: 6 }}>
          {filtered.length === 0 && <div className="muted" style={{ padding: 12, fontSize: 13 }}>{tr('Nessuna pagina.')}</div>}
          {filtered.map((p) => (
            <button key={p.id} className="nav-item" onClick={() => onPick(p.id)} style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 18 }}>{p.isFolder ? '📁' : (p.icon || '📄')}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{p.title}</span>
                <span className="muted mono" style={{ fontSize: 11 }}>{p.slug}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function History({ page, onClose }: { page: WikiPage; onClose: () => void }) {
  const tr = useT();
  const versions = page.versions ?? [];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'grid', placeItems: 'center', zIndex: 120, padding: 20 }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(520px, 100%)', padding: 0 }}>
        <div className="spaced" style={{ padding: '14px 18px', borderBottom: '1px solid var(--line,#eee)' }}>
          <div><strong>{tr('Cronologia versioni')}</strong><div className="muted" style={{ fontSize: 12 }}>{page.title}</div></div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div style={{ maxHeight: 380, overflow: 'auto', padding: 12 }}>
          {versions.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{tr('Nessuna versione registrata.')}</div>}
          {versions.map((v) => (
            <div key={v.v} className="row" style={{ gap: 12, padding: '10px 8px', borderBottom: '1px solid var(--line,#f0f0f0)', alignItems: 'flex-start' }}>
              <span className="badge" style={{ marginTop: 2 }}>v{v.v}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{v.message}</div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{v.author} · {v.date}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function WikiScreen() {
  const { lang, t: tr } = useLang();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const sideRef = useRef<HTMLDivElement>(null);
  const auth = useAuth();
  const q = useWiki();
  const employeesQ = useEmployees();
  const createPage = useCreateWikiPage();
  const updatePage = useUpdateWikiPage();
  const reorder = useReorderWiki();
  // The API rejects reorder for anyone else, so hide the affordance too.
  const canReorder = auth.hasRole(Roles.ADMIN) || auth.hasRole(Roles.TEAM_LEAD);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [treeQuery, setTreeQuery] = useState('');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftIcon, setDraftIcon] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftTags, setDraftTags] = useState('');
  const [draftOwner, setDraftOwner] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sideOpen, setSideOpen] = useState(false); // mobile page-tree drawer
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  useDialogFocus(sideRef, sideOpen, () => setSideOpen(false));

  const pages = q.data ?? [];
  const employees = employeesQ.data?.items ?? [];
  const current = pages.find((p) => p.id === currentId) ?? pages[0] ?? null;
  const ownerName = (id: string | null) => employees.find((e) => e.id === id)?.name;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setShowSwitcher(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const breadcrumb = useMemo(() => {
    const path: WikiPage[] = [];
    let p: WikiPage | undefined = current ?? undefined;
    while (p) { path.unshift(p); p = p.parentId ? pages.find((x) => x.id === p!.parentId) : undefined; }
    return path;
  }, [current, pages]);

  const bodyHtml = useMemo(() => (current ? renderBody(current.body, pages) : ''), [current, pages]);
  const toc = useMemo(() => tocFromHtml(bodyHtml), [bodyHtml]);
  const backlinks = useMemo(() => {
    if (!current) return [];
    const re = new RegExp(`(\\[\\[${current.slug}(?:\\||\\]\\])|data-wiki-slug="${current.slug}")`);
    return pages.filter((p) => p.id !== current.id && re.test(p.body));
  }, [current, pages]);
  const stats = useMemo(() => {
    const words = htmlToText(bodyHtml).split(/\s+/).filter(Boolean).length;
    return { words, minutes: Math.max(1, Math.round(words / 200)) };
  }, [bodyHtml]);

  function goSlug(slug: string) { const p = pages.find((x) => x.slug === slug); if (p) { setCurrentId(p.id); setMode('view'); } }
  function onBodyClick(e: MouseEvent) {
    const a = (e.target as HTMLElement).closest('a');
    if (!a) return;
    const slug = a.getAttribute('data-wiki-slug');
    if (slug) {
      e.preventDefault();
      goSlug(slug);
    } else if (a.classList.contains('is-broken')) {
      // Missing wiki targets are informational; "#" must not reset app routing.
      e.preventDefault();
    }
  }

  async function newPage(parentId?: string, isFolder = false) {
    const base = isFolder ? tr('Nuova cartella') : tr('Nuova pagina');
    const created = await createPage.mutateAsync({
      slug: slugify(`${base}-${Date.now().toString().slice(-4)}`),
      title: base, isFolder, parentId,
      // Empty so the editor shows a real placeholder instead of text to delete.
      body: '',
    });
    if (parentId) setExpanded((x) => ({ ...x, [parentId]: true }));
    setCurrentId(created.id); setMode('view');
  }
  function enterEdit() {
    if (!current) return;
    setDraftTitle(current.title); setDraftIcon(current.icon ?? ''); setDraftBody(current.body);
    setDraftTags(current.tags.join(', ')); setDraftOwner(current.ownerId ?? ''); setMode('edit');
  }
  async function saveEdit() {
    if (!current) return;
    await updatePage.mutateAsync({
      id: current.id,
      title: draftTitle.trim() || current.title,
      icon: draftIcon.trim() || undefined,
      body: draftBody,
      tags: draftTags.split(',').map((t) => t.trim()).filter(Boolean),
      ownerId: draftOwner || '',
      versionMessage: tr('Aggiornamento'),
    });
    setMode('view');
  }
  function toggleStar() { if (current) updatePage.mutate({ id: current.id, starred: !current.starred }); }

  return (
    <Query query={q}>
      {() => {
        if (!current) {
          return (
            <div style={{ padding: 24 }}>
              <div className="card" style={{ padding: 24, maxWidth: 420 }}>
                <h2 className="h2" style={{ marginBottom: 6 }}>{tr('Wiki vuota')}</h2>
                <p className="muted" style={{ margin: '0 0 14px' }}>{tr('Crea la prima pagina della knowledge base.')}</p>
                <button className="btn btn--brand" onClick={() => newPage()}><Icon name="plus" size={13} /> {tr('Nuova pagina')}</button>
              </div>
            </div>
          );
        }
        return (
          <div className="wiki">
            {sideOpen && <div className="wiki-side__backdrop" onClick={() => setSideOpen(false)} />}
            <div
              ref={sideRef}
              id="wiki-page-tree"
              className={`wiki-side ${sideOpen ? 'is-open' : ''}`}
              role={sideOpen ? 'dialog' : undefined}
              aria-modal={sideOpen ? 'true' : undefined}
              aria-labelledby="wiki-browser-title"
              tabIndex={-1}
            >
              <div className="wiki-side__head">
                <div className="wiki-side__title">
                  <span className="wiki-side__title-icon"><Icon name="book" size={16} /></span>
                  <span><strong id="wiki-browser-title">{tr('Pagine Wiki')}</strong><small>{tr('{n} pagine e cartelle', { n: pages.length })}</small></span>
                </div>
                <div className="row" style={{ gap: 2 }}>
                  <button className="btn btn--sm btn--icon" title={tr('Nuova cartella')} aria-label={tr('Nuova cartella')} onClick={() => { setSideOpen(false); void newPage(undefined, true); }}><Icon name="folder" size={14} /></button>
                  <button className="btn btn--sm btn--icon" title={tr('Nuova pagina')} aria-label={tr('Nuova pagina')} onClick={() => { setSideOpen(false); void newPage(); }}><Icon name="plus" size={14} /></button>
                  <button className="btn btn--sm btn--icon wiki-side__close" title={tr('Chiudi')} aria-label={tr('Chiudi pagine Wiki')} onClick={() => setSideOpen(false)}><Icon name="x" size={15} /></button>
                </div>
              </div>
              <div className="wiki-side__search">
                <Icon name="search" size={13} />
                <input data-dialog-initial-focus aria-label={tr('Cerca pagine e tag')} placeholder={tr('Cerca pagine, tag…')} value={treeQuery} onChange={(e) => setTreeQuery(e.target.value)} />
                {treeQuery && <button className="wiki-side__clear" onClick={() => setTreeQuery('')} aria-label={tr('Cancella ricerca')}><Icon name="x" size={13} /></button>}
                <button className="kbd" onClick={() => setShowSwitcher(true)} title={tr('Vai a…')}>⌘K</button>
              </div>
              <Tree pages={pages} currentId={current.id} query={treeQuery}
                    expanded={expanded} onToggle={(id) => setExpanded((x) => ({ ...x, [id]: x[id] === false }))}
                    onSelect={(id) => { setCurrentId(id); setMode('view'); setSideOpen(false); }}
                    onAddSub={(pid) => { setSideOpen(false); void newPage(pid); }}
                    canReorder={canReorder} onMove={(items) => reorder.mutate(items)} />
            </div>

            <div className="wiki-main">
              {/* Mobile-only: open the page tree (hidden on desktop). */}
              <button
                className="wiki-browse"
                aria-controls="wiki-page-tree"
                aria-expanded={sideOpen}
                onClick={() => setSideOpen(true)}
              >
                <span className="wiki-browse__leading"><Icon name="folder" size={17} /></span>
                <span className="wiki-browse__copy">
                  <strong>{tr('Esplora le pagine')}</strong>
                  <small>{breadcrumb.length > 1 ? breadcrumb.slice(0, -1).map((item) => item.title).join(' / ') : tr('Primo livello')}</small>
                </span>
                <Icon name="chevR" size={16} />
              </button>
              {/* Compact header: small cover strip, tight spacing above the title. */}
              <div className="wiki-page" style={{ paddingTop: 14 }}>
                <div className="wiki-breadcrumb" style={{ marginBottom: 8 }}>
                  {breadcrumb.map((b, i) => (
                    <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {i > 0 && <Icon name="chevR" size={11} />}
                      {i === breadcrumb.length - 1
                        ? <span className="wiki-breadcrumb__here">{b.isFolder ? '📁' : b.icon} {b.title}</span>
                        : <button className="wiki-breadcrumb__link" onClick={() => { setCurrentId(b.id); setMode('view'); }}>{b.isFolder ? '📁' : b.icon} {b.title}</button>}
                    </span>
                  ))}
                </div>

                <nav className={`wiki-pagebar wiki-pagebar--${mode}`} style={{ marginBottom: 14 }} aria-label={tr('Azioni pagina')}>
                  {mode === 'view' ? (
                    <div className="wiki-pagebar__actions">
                      <button
                        className={`wiki-page-action wiki-page-action--star ${current.starred ? 'is-active' : ''}`}
                        onClick={toggleStar}
                        aria-label={current.starred ? tr('Rimuovi dai preferiti') : tr('Aggiungi ai preferiti')}
                        aria-pressed={current.starred}
                      >
                        <Icon name="star" size={15} />
                        <span className="wiki-page-action__desktop-label">{tr('Preferito')}</span>
                      </button>
                      <button className="wiki-page-action" onClick={() => setShowHistory(true)} aria-label={tr('Cronologia, versione {v}', { v: current.versions?.[0]?.v ?? 1 })}>
                        <Icon name="refresh" size={14} />
                        <span className="wiki-page-action__desktop-label">{tr('Cronologia · ')}</span>
                        <span>v{current.versions?.[0]?.v ?? 1}</span>
                      </button>
                      <button className="wiki-page-action" onClick={() => setShowSwitcher(true)}>
                        <Icon name="search" size={14} /><span>{tr('Vai a')}</span>
                      </button>
                      <button className="wiki-page-action is-primary" onClick={enterEdit}>
                        <Icon name="edit" size={14} /><span className="wiki-page-action__edit-label">{tr('Modifica')}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="wiki-pagebar__edit-actions">
                      <button className="wiki-page-action" onClick={() => setMode('view')}>{tr('Annulla')}</button>
                      <button className="wiki-page-action is-primary" onClick={saveEdit} disabled={updatePage.isPending}><Icon name="check" size={14} /> {tr('Salva versione')}</button>
                    </div>
                  )}
                </nav>

                {mode === 'view' ? (
                  <>
                    <div className="wiki-title-row" style={{ marginBottom: 6 }}>
                      <span className="wiki-icon-input">{current.isFolder ? '📁' : (current.icon || '📄')}</span>
                      <h1 className="wiki-title" style={{ margin: 0 }}>{current.title}</h1>
                    </div>
                    <div className="wiki-meta">
                      <span className="wiki-meta__author">
                        <span className="avatar xs" style={{ background: 'var(--bg-sunk)', color: 'var(--ink-3)' }}>{initials(current.updatedBy || current.author || 'TS')}</span>
                        <span>{tr('Aggiornata da')} <b>{current.updatedBy || current.author || '—'}</b> · {new Date(current.updatedAt).toLocaleDateString(loc)}</span>
                      </span>
                      {ownerName(current.ownerId) && <><span className="muted">·</span><span>{tr('Responsabile:')} <b>{ownerName(current.ownerId)}</b></span></>}
                      <span className="muted">·</span>
                      <span className="muted">{tr('{w} parole · ~{m} min', { w: stats.words, m: stats.minutes })}</span>
                      {current.tags.length > 0 && <><span className="muted">·</span><div className="cluster" style={{ gap: 4 }}>{current.tags.map((t) => <span key={t} className="wiki-tag">#{t}</span>)}</div></>}
                    </div>

                    <div className={`wiki-content-layout ${toc.length > 0 ? 'has-toc' : ''}`}>
                      <div className="wiki-content-main">
                        {current.isFolder && htmlToText(bodyHtml) === '' ? (
                          <p className="muted">{tr('Questa è una cartella. Le pagine al suo interno sono elencate qui sotto.')}</p>
                        ) : (
                          <div className="md-body wiki-body" onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                        )}

                        {(() => {
                          const subs = pages.filter((p) => p.parentId === current.id);
                          if (subs.length === 0) return null;
                          return (
                            <div className="wiki-subpages">
                              <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: 'var(--ink-3)' }}>{tr('Pagine ({n})', { n: subs.length })}</h3>
                              <div className="grid grid-3">
                                {subs.map((s) => (
                                  <button key={s.id} className="wiki-subpage-card" onClick={() => setCurrentId(s.id)}>
                                    <div className="wiki-subpage-card__icon">{s.isFolder ? '📁' : (s.icon || '📄')}</div>
                                    <div><div className="wiki-subpage-card__title">{s.title}</div><div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{tr('agg. {date}', { date: new Date(s.updatedAt).toLocaleDateString(loc) })}</div></div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {backlinks.length > 0 && (
                          <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--line,#eee)' }}>
                            <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--ink-3)' }}>{tr('Collegamenti in entrata')}</h3>
                            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                              {backlinks.map((p) => <button key={p.id} className="btn btn--sm" onClick={() => setCurrentId(p.id)}>{p.icon || '📄'} {p.title}</button>)}
                            </div>
                          </div>
                        )}
                      </div>

                      {toc.length > 0 && (
                        <aside className="wiki-content-toc">
                          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 8 }}>{tr('In questa pagina')}</div>
                          <div className="col" style={{ gap: 4 }}>
                            {toc.map((t) => (
                              <a key={t.id} href={`#${t.id}`} className="muted" style={{ fontSize: 12.5, textDecoration: 'none', paddingLeft: (t.level - 1) * 10 }}
                                 onClick={(e) => { e.preventDefault(); document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                                {t.text}
                              </a>
                            ))}
                          </div>
                        </aside>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="wiki-body-wrap">
                    <div className="row" style={{ gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input className="input" style={{ width: 60, textAlign: 'center' }} maxLength={8} value={draftIcon} onChange={(e) => setDraftIcon(e.target.value)} placeholder="📄" title={tr('Emoji icona')} />
                      <input className="input" style={{ flex: 2, minWidth: 200, fontSize: 18, fontWeight: 700 }} value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} placeholder={tr('Titolo pagina')} />
                    </div>
                    <div className="row" style={{ gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <input className="input" style={{ flex: 1, minWidth: 200 }} value={draftTags} onChange={(e) => setDraftTags(e.target.value)} placeholder={tr('Tag separati da virgola')} />
                      <select className="input" style={{ flex: 1, minWidth: 200 }} value={draftOwner} onChange={(e) => setDraftOwner(e.target.value)}>
                        <option value="">{tr('Responsabile: nessuno')}</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{tr('Responsabile: {name}', { name: e.name })}</option>)}
                      </select>
                    </div>
                    <div className="wiki-edit-help">
                      <Icon name="info" size={12} />
                      <span>Barra: formattazione, <b>tabelle</b>, <b>immagini/video</b> (anche <b>drag &amp; drop</b> o incolla), <b>link</b> e <b>collega pagina</b>. Il tasto <b>MD</b> mostra il sorgente Markdown, modificabile a mano.</span>
                    </div>
                    <RichTextEditor value={draftBody} onChange={setDraftBody} minHeight={420} placeholder={tr('Inizia a scrivere…')} format="markdown"
                      wikiPages={pages.filter((p) => p.id !== current.id && !p.isFolder).map((p) => ({ slug: p.slug, title: p.title, icon: p.icon }))} />
                  </div>
                )}
              </div>
            </div>

            {showSwitcher && <Switcher pages={pages} title={tr('Vai a una pagina…')} onPick={(id) => { setCurrentId(id); setMode('view'); setShowSwitcher(false); }} onClose={() => setShowSwitcher(false)} />}
            {showHistory && <History page={current} onClose={() => setShowHistory(false)} />}
          </div>
        );
      }}
    </Query>
  );
}
